// Offline stories bundle loader for the Bible-search arc, Plan 05 (Layer
// 4). This module is the boundary the search UI reads stories through,
// parallel to rule #65's facade discipline for BSB data and to the chunk-04
// topics.ts loader: the UI never fetches /bible-search/stories.json directly
// nor imports story-search.ts itself, it goes through here.
//
// The bundle (public/bible-search/stories.json) is built offline by
// scripts/build-bible-search-stories.mjs from scripts/spine-catalog/ (rule
// #4 cross-boundary: the catalog is the source of truth, this static
// artifact is the offline duplicate). Only auto_pair_safe rows are emitted
// (the safety gate runs at build time, roadmap decision 3 + rule #76). It
// carries verse REFS only (the pivotRef string); the pivot verse TEXT
// resolves from bundled BSB at render via the bible/active facade (rule
// #62/#76). en-US content only at v1 (rule #7); search chrome is i18n'd
// separately. When es/pt stories are authored, a per-locale bundle path
// (e.g. /bible-search/stories.es.json) is added here.
//
// The pure matchers (searchStories, storiesForTopic, storyId) + types live
// in the sibling story-search.ts leaf (probe-importable, zero browser deps)
// and are re-exported here so the UI gets the loader + the matchers from one
// boundary, mirroring how topics.ts re-exports searchTopics.

import { searchStories, searchStoriesByName, storiesForTopic, storyId } from './story-search';
import type { BundledStory, StoriesBundle } from './story-search';

// Must match BUNDLE_VERSION in scripts/build-bible-search-stories.mjs. A
// future shape/content change bumps both; the loader rejects a bundle whose
// version it does not understand so the stories layer fails closed (the
// verse + topic + reference layers keep working) rather than mis-rendering.
// v2 (2026-06-11): records carry doNotPairWith for the tap-to-match
// saved-result safety gate (src/lib/spine-clusters.ts).
const SUPPORTED_BUNDLE_VERSION = 2;

const BUNDLE_URL = '/bible-search/stories.json';

interface StoriesBundleEnvelope {
  version: number;
  locale: string;
  stories: BundledStory[];
  byTopic: Record<string, number[]>;
}

let cache: Promise<StoriesBundle> | null = null;
let resolved: StoriesBundle | null = null;

function parseBundle(data: StoriesBundleEnvelope): StoriesBundle {
  if (data.version !== SUPPORTED_BUNDLE_VERSION) {
    throw new Error(
      `unsupported stories bundle version ${data.version} (expected ${SUPPORTED_BUNDLE_VERSION})`,
    );
  }
  if (!Array.isArray(data.stories)) {
    throw new Error('stories bundle missing stories array');
  }
  if (!data.byTopic || typeof data.byTopic !== 'object') {
    throw new Error('stories bundle missing byTopic index');
  }
  return { stories: data.stories, byTopic: data.byTopic };
}

async function fetchBundle(): Promise<StoriesBundle> {
  const res = await fetch(BUNDLE_URL);
  if (!res.ok) throw new Error(`stories bundle fetch failed: ${res.status}`);
  return parseBundle(await res.json() as StoriesBundleEnvelope);
}

/**
 * Lazy, module-cached stories load (mirrors topics.ts / bsb-search.ts: cache
 * the Promise, clear it on rejection so a transient failure does not poison
 * every subsequent call). Fetched once on first story-eligible search and
 * served from the service worker cache thereafter (offline after first use).
 */
export async function loadStories({ localOnly = false }: { localOnly?: boolean } = {}): Promise<StoriesBundle> {
  // Public search stays available before owner verification. Read the SW cache
  // directly: fetch() may start a network request even on an offline cold open.
  if (localOnly) {
    if (resolved) return resolved;
    const response = typeof caches === 'undefined' ? undefined : await caches.match(BUNDLE_URL);
    return response ? parseBundle(await response.json() as StoriesBundleEnvelope) : { stories: [], byTopic: {} };
  }
  if (!cache) {
    cache = fetchBundle().then((data) => { resolved = data; return data; }).catch((err) => {
      cache = null;
      throw err;
    });
  }
  return cache;
}

/** Test/recovery hook: drop the cached bundle so the next load refetches. */
export function clearStoriesCache(): void {
  cache = null;
  resolved = null;
}

export { searchStories, searchStoriesByName, storiesForTopic, storyId };
export type { BundledStory, StoriesBundle };
